import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { PortalWrapper } from './PortalWrapper';

/**
 * Inspiration from https://github.com/sunify/react-relative-portal
 * A fork was needed to support z-index to show above the modals
 * Allows for the relative component to take up the full width of the parent element, in React Semantic terms the "fluid" prop
 */

const listeners = {};

function fireListeners() {
  Object.keys(listeners).forEach(key => listeners[key]());
}

function getPageOffset() {
  return {
    x:
      window.pageXOffset !== undefined
        ? window.pageXOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollLeft,
    y:
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollTop,
  };
}

let initiated = false;

function initDOMListener() {
  document.body.addEventListener(
    'mousewheel',
    _.throttle(fireListeners, 100, {
      leading: true,
      trailing: true,
    })
  );
  window.addEventListener(
    'resize',
    _.throttle(fireListeners, 50, {
      leading: true,
      trailing: true,
    })
  );
  initiated = true;
}

let listenerIdCounter = 0;
function subscribe(fn) {
  listenerIdCounter += 1;
  const id = listenerIdCounter;
  listeners[id] = fn;
  return () => delete listeners[id];
}

class RelativePortal extends React.Component {
  static propTypes = {
    right: PropTypes.number,
    left: PropTypes.number,
    top: PropTypes.number,
    fluid: PropTypes.bool,
    children: PropTypes.any,
    onOutClick: PropTypes.func,
    component: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
      .isRequired,
  };

  static defaultProps = {
    left: 0,
    top: 0,
    right: 0,
    component: 'div',
    fluid: false,
    onOutClick: () => {},
  };

  state = {
    right: 0,
    left: 0,
    top: 0,
  };

  relativeMountNode = React.createRef();
  childContainer = React.createRef();

  constructor(props) {
    super(props);
    if (!initiated) {
      initDOMListener();
    }
  }

  get mountNode() {
    if (this.relativeMountNode && this.relativeMountNode.current) {
      return this.relativeMountNode.current;
    }
  }

  get height() {
    if (this.childContainer.current) {
      return this.childContainer.current.clientHeight;
    }
    return 0;
  }

  componentDidMount() {
    this.handleScroll();
  }

  handleScroll = () => {
    if (this.mountNode) {
      const rect = this.mountNode.getBoundingClientRect();
      const pageOffset = getPageOffset();
      const top = pageOffset.y + rect.top;
      const right =
        document.documentElement.clientWidth - rect.right - pageOffset.x;
      const left = pageOffset.x + rect.left;

      if (
        top !== this.state.top ||
        left !== this.state.left ||
        right !== this.state.right
      ) {
        this.setState({ left, top, right });
      }
    }
  };
  unsubscribe = subscribe(this.handleScroll);

  componentDidUpdate() {
    requestAnimationFrame(this.handleScroll);
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  render() {
    const {
      component: RelativeMountNode,
      top,
      left,
      right,
      parentRef,
      fluid,
      children,
      ...rest
    } = this.props;

    const fromLeftOrRight =
      right !== undefined
        ? { right: this.state.right + right }
        : { left: this.state.left + left };

    const horizontalPosition = fluid
      ? { right: this.state.right + right, left: this.state.left + left }
      : fromLeftOrRight;

    const style = {
      position: 'absolute',
      top: this.state.top + top,
      zIndex: 1000000,
      ...horizontalPosition,
    };

    const mountNodeStyle = {};

    if (this.height) {
      mountNodeStyle.height = this.height;
    }

    return (
      <RelativeMountNode style={mountNodeStyle} ref={this.relativeMountNode}>
        <PortalWrapper {...rest}>
          <div ref={this.childContainer} style={style}>
            {children}
          </div>
        </PortalWrapper>
      </RelativeMountNode>
    );
  }
}

export { RelativePortal };



// WEBPACK FOOTER //
// ./src/common/Components/RelativePortal/RelativePortal.js